import {v3} from "oc-threejs/short3";
import BohrlochleisteO3 from "../object3d/Bohrlochleiste";
import Maths3 from "oc-threejs/Maths3";
import Commons from "oc-commons/Commons";

export default class Bohrlochleiste {
    // DO NOT CHANGE
    className = "Bohrlochleiste";

    // persistent (UserLayer) ------------------------------------------

    // transient (CompiledLayer) ---------------------------------------
    
    object3d: ?THREE.Object3D;
    cfg: ?Object;

    constructor(element) {
        this.element = element;
    }

    compile() {
        APP.model.lazyCreateObject3d(this, () => {
            const o3 = new BohrlochleisteO3(this);
            this.element.child3dContainer.add(o3);
            return o3;
        });
    }

    get schrank() {
        return this.element.schrank;
    }

    get elementBox() {
        return this.element.box;
    }

    get interieurSpecificBohrlochBounds() {
        const bounds = this.getBohrlochBounds();
        const cfg = this.cfg;
        if (cfg.type!=='Kleiderstange') {
            return bounds;
        }
        const ys = this.getBohrlochYSchrankPositions();
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        this.element.interieurs.filter(i=>i.type==='Kleiderstange').forEach(i=>{
            const yi = ys.findIndex(y=>Math.abs(i.position.y-y)<cfg.abstand/2);
            if (yi===-1) {
                return;
            }
            for (let j=Math.max(0,yi-cfg.max_loecher_anzahl_unten);j<=Math.min(ys.length-1,yi+cfg.max_loecher_anzahl_oben+1);j++) {
                const y = ys[j];
                min = Math.min(min,y);
                max = Math.max(max,y);
            }
        });
        bounds.min.y = Math.max(bounds.min.y,min-cfg.abstand/2);
        bounds.max.y = Math.min(bounds.max.y,max-cfg.abstand/2);
        return bounds;
    }

    get cfgId() {
        return this.cfg.id;
    }

    getBohrlochBounds() {
        const bounds = this.elementBox.clone();
        const cfg = this.cfg;
        // console.log(this.cfg);
        bounds.min.y += cfg.abstand_boden;
        bounds.max.y -= cfg.abstand_deckel;
        bounds.max.y = Math.max(0,Math.min(bounds.max.y,this.element.slopeTop-cfg.mindesthoehe_oben));
        return bounds;
    }

    getBohrlochYSchrankPositions() {
        const ys = [];
        const bounds = this.getBohrlochBounds();
        for (let y = bounds.min.y; y + (this.cfg.abstand + this.cfg.durchmesser)/2 <= bounds.max.y; y += this.cfg.abstand) {
            const yLocal = y+this.cfg.abstand/2;
            const ySchrank = this.element.localToSchrank(v3(0,yLocal,0)).y;
            ys.push(ySchrank);
        }
        return ys;
    }

    static createBohrlochleiste(element,type) {
        const bohrlochleiste = new Bohrlochleiste(element);
        const cfgArray = Bohrlochleiste.lochleistenCfg(element.schrank)[type];
        bohrlochleiste.cfg = cfgArray[0];
        return bohrlochleiste;
    }

    static lochleistenCfg = Commons.cache((schrank)=>{

        let id = 0;

        const einlegebodenCfg = {
            durchmesser: schrank.options.getFloat('einlegeboden_bohrloch_durchmesser'),
            abstand: schrank.options.getFloat('einlegeboden_bohrloch_abstand'),
            abstand_boden: schrank.options.getFloat('einlegeboden_bohrloch_abstand_boden'),
            abstand_deckel: schrank.options.getFloat('einlegeboden_bohrloch_abstand_deckel'),
            abstand_vorne: schrank.options.getFloat('einlegeboden_bohrloch_abstand_vorne'),
            abstand_hinten: schrank.options.getFloat('einlegeboden_bohrloch_abstand_hinten'),
            mindesthoehe_oben: schrank.options.getFloat('einlegeboden_mindesthoehe_fach') / 2,
            mindesthoehe_unten: schrank.options.getFloat('einlegeboden_mindesthoehe_fach') / 2,
            opacity: schrank.options.getFloat('bohrloch_opacity'),
            max_loecher_anzahl_oben: Number.POSITIVE_INFINITY,
            max_loecher_anzahl_unten: Number.POSITIVE_INFINITY,
        };

        const kleiderstangeCfg = {
            durchmesser: schrank.options.getFloat('kleiderstange_bohrloch_durchmesser'),
            abstand: schrank.options.getFloat('kleiderstange_bohrloch_abstand'),
            abstand_boden: schrank.options.getFloat('kleiderstange_bohrloch_abstand_boden'),
            abstand_deckel: schrank.options.getFloat('kleiderstange_bohrloch_abstand_deckel'),
            mindesthoehe_oben: 0,
            mindesthoehe_unten: 0,
            opacity: schrank.options.getFloat('bohrloch_opacity'),
            max_loecher_anzahl_oben: 2,
            max_loecher_anzahl_unten: 12,
        };

        const einlegebodenLoecher = (normal, offset, quaternion) => Commons.defaultsDeepSafe({}, einlegebodenCfg, {
            normal,
            offset,
            quaternion,
            type: 'Einlegeboden',
            id: id++,
        });

        const kleiderstangenLoecher = (normal, offset, quaternion) => Commons.defaultsDeepSafe({}, kleiderstangeCfg, {
            normal,
            offset,
            quaternion,
            type: 'Kleiderstange',
            id: id++,
        });

        const xDistance = 0.1;

        return {
            Kleiderstange: [
                kleiderstangenLoecher(v3(-1, 0, 0), v3(xDistance, 0, 0), Maths3.quaternion.y90),
                kleiderstangenLoecher(v3(+1, 0, 0), v3(xDistance, 0, 0), Maths3.quaternion.y270),
            ],
            Einlegeboden: [
                einlegebodenLoecher(v3(-1, 0, +1), v3(xDistance, 0, einlegebodenCfg.abstand_vorne), Maths3.quaternion.y90),
                einlegebodenLoecher(v3(-1, 0, -1), v3(xDistance, 0, einlegebodenCfg.abstand_hinten), Maths3.quaternion.y90),
                einlegebodenLoecher(v3(+1, 0, +1), v3(xDistance, 0, einlegebodenCfg.abstand_vorne), Maths3.quaternion.y270),
                einlegebodenLoecher(v3(+1, 0, -1), v3(xDistance, 0, einlegebodenCfg.abstand_hinten), Maths3.quaternion.y270),
            ]
        };
        
    }).bind(this);

}
